import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Badge,
  Table,
  Input,
  Spinner,
} from "reactstrap";

import { ImDownload } from "react-icons/im";
import { IoEye } from "react-icons/io5";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import {
  FaArrowAltCircleLeft,
  FaArrowAltCircleRight,
  FaDownload,
  FaFilter,
  FaPlus,
} from "react-icons/fa";
import {
  BsFillArrowDownSquareFill,
  BsFillArrowUpSquareFill,
} from "react-icons/bs";

import swal from "sweetalert";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Papa from "papaparse";
import * as XLSX from "xlsx";

// NOTE: Per your instruction, shared modules live in ../import/
import {
  _Get,
  _GetList,
  DeleteAccount, // (kept if you still need delete flow)
  Stockupdate,
} from "../import/ApiEndPoint/ApiCalling";
import {
  Stock_trx_FtoW_List,
  Warehouse_ListBy_id,
} from "../import/ApiEndPoint/Api";

import StockTrxInvoice from "../import/InwardTrxInvoice";
// If you keep a logo, import it locally or remove the addImage() usage in PDF:
import Logo from "../import/logowithoutback.png";

// --- Helpers ---
const toCSV = (rows, visibleCols) => {
  const headers = visibleCols.map((c) => c.headerName);
  const data = rows.map((r) =>
    visibleCols.map((c) =>
      typeof c.value === "function"
        ? c.value(r)
        : c.valuePath
        ? c.valuePath.split(".").reduce((o, k) => (o || {})[k], r)
        : r[c.field]
    )
  );
  return Papa.unparse([headers, ...data]);
};

const toXLSXBlob = (rows, visibleCols) => {
  const sheetRows = rows.map((r) => {
    const obj = {};
    visibleCols.forEach((c) => {
      const header = c.headerName;
      const value =
        typeof c.value === "function"
          ? c.value(r)
          : c.valuePath
          ? c.valuePath.split(".").reduce((o, k) => (o || {})[k], r)
          : r[c.field];
      obj[header] = value ?? "";
    });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(sheetRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

// --- Component ---
const WarehouseStockTransfer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [rows, setRows] = useState([]); // table data
  const [search, setSearch] = useState(""); // quick search
  const [isExportOpen, setIsExportOpen] = useState(false); // export dropdown
  const [colModal, setColModal] = useState(false); // choose columns modal

  // role checks
  const [warehouseIncharge, setWarehouseIncharge] = useState(false);
  const [superAdmin, setSuperAdmin] = useState(false);

  // View modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [showBill, setShowBill] = useState(false);

  // Columns
  const baseColumns = useMemo(
    () => [
      {
        headerName: "S.No",
        field: "__idx",
        value: (_r, i) => i + 1,
        width: 55,
      },
      {
        headerName: "Actions",
        field: "__actions",
        width: 80,
        render: (r) => (
          <IoEye
            size={20}
            color="green"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setViewRow(r);
              setShowBill(false);
              setViewOpen(true);
            }}
          />
        ),
      },
      {
        headerName: "Status",
        field: "transferStatus",
        width: 100,
      },
      {
        headerName: "Trx Date",
        field: "stockTransferDate",
        width: 100,
      },
      {
        headerName: "Request Id",
        field: "warehouseNo",
        width: 170,
      },
      {
        headerName: "Total Product",
        field: "productItems",
        value: (r) => `${(r?.productItems || []).length} Products`,
      },
      {
        headerName: "Transferred From",
        field: "warehouseFromId",
        valuePath: "warehouseFromId.warehouseName",
      },
      {
        headerName: "Transferred To",
        field: "warehouseToId",
        valuePath: "warehouseToId.warehouseName",
      },
      {
        headerName: "Mobile no Transfer To",
        field: "warehouseToId.mobileNo",
        valuePath: "warehouseToId.mobileNo",
        width: 180,
      },
      {
        headerName: "Created date",
        field: "createdAt",
        value: (r) => (r?.createdAt ? String(r.createdAt).split("T")[0] : ""),
        width: 110,
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        value: (r) => (r?.updatedAt ? String(r.updatedAt).split("T")[0] : ""),
        width: 110,
      },
    ],
    []
  );

  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("stockTransfer"));
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {}
    return baseColumns;
  });

  const [availableCols, setAvailableCols] = useState(baseColumns);
  const [selectedPool, setSelectedPool] = useState([]); // temp selected to add
  const [activeIndex, setActiveIndex] = useState(-1); // for up/down order

  // Pagination (client)
  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // Load & role logic
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        const isWHI = user?.rolename?.roleName === "WareHouse Incharge";
        const isSA = user?.rolename?.roleName === "SuperAdmin";
        setWarehouseIncharge(isWHI);
        setSuperAdmin(isSA);

        const listRes = await _Get(Stock_trx_FtoW_List, user?.database);
        let list = (listRes?.Warehouse || []).reverse();

        if (isWHI) {
          const url = `${Warehouse_ListBy_id + user?._id}/${user?.database}`;
          const wRes = await _GetList(url);
          const assignedIds = (wRes?.Warehouse || []).map((w) => w?._id);

          list = list.filter(
            (it) =>
              assignedIds.includes(it?.warehouseToId?._id) ||
              assignedIds.includes(it?.warehouseFromId?._id)
          );

          list = list.map((it) => ({
            ...it,
            ShowDropDown: assignedIds.includes(it?.warehouseToId?._id),
          }));
        } else if (isSA) {
          list = list.map((it) => ({ ...it, ShowDropDown: true }));
        } else {
          list = list.map((it) => ({ ...it, ShowDropDown: false }));
        }

        setRows(list);
        setAvailableCols(baseColumns);
      } catch (e) {
        setRows([]);
        // console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export handlers
  const exportPDF = () => {
    if (!pageData.length) return;
    const doc = new jsPDF("landscape", "mm", "a4");
    try {
      if (Logo) doc.addImage(Logo, "PNG", 10, 10, 40, 20);
    } catch {}
    doc.setFontSize(12);
    doc.text("Stock Transfer", 14, 40);

    const headers = [visibleCols.map((c) => c.headerName)];
    const body = filtered.map((r) =>
      visibleCols.map((c) =>
        typeof c.value === "function"
          ? c.value(r, r.__idx)
          : c.valuePath
          ? c.valuePath.split(".").reduce((o, k) => (o || {})[k], r) ?? ""
          : r[c.field] ?? ""
      )
    );
    doc.autoTable({
      head: headers,
      body,
      startY: 50,
      styles: { fontSize: 8 },
    });

    doc.save("StockTransfer.pdf");
  };

  const exportCSV = () => {
    const csv = toCSV(filtered, visibleCols);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, "StockTransfer.csv");
  };

  const exportXLS = () => {
    // XLS (BIFF8) not natively supported by xlsx lib for writeFile with buffer easily; emulate via CSV->XLS if needed.
    const csv = toCSV(filtered, visibleCols);
    Papa.parse(csv, {
      complete: (result) => {
        const ws = XLSX.utils.json_to_sheet(result.data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        XLSX.writeFile(wb, "StockTransfer.xls");
      },
    });
  };

  const exportXLSX = () => {
    const blob = toXLSXBlob(filtered, visibleCols);
    downloadBlob(blob, "StockTransfer.xlsx");
  };

  const exportXML = () => {
    const csv = toCSV(filtered, visibleCols);
    Papa.parse(csv, {
      complete: (result) => {
        const rows = result.data || [];
        let xml = "<root>\n";
        rows.forEach((row, ri) => {
          xml += "  <row>\n";
          row.forEach((cell, ci) => {
            xml += `    <field${ci + 1}>${String(cell ?? "")
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")}</field${ci + 1}>\n`;
          });
          xml += "  </row>\n";
        });
        xml += "</root>";
        const blob = new Blob([xml], { type: "text/xml" });
        downloadBlob(blob, "StockTransfer.xml");
      },
    });
  };

  // Column modal handlers
  const onToggleColModal = () => setColModal((s) => !s);
  const onAddCols = () => {
    const next = [
      ...new Set([
        ...visibleCols.map((c) => c.headerName),
        ...selectedPool.map((c) => c.headerName),
      ]),
    ].map((h) => availableCols.find((c) => c.headerName === h));
    setVisibleCols(next.filter(Boolean));
    setSelectedPool([]);
  };
  const onRemoveActive = () => {
    if (activeIndex < 0) return;
    const next = visibleCols.slice();
    next.splice(activeIndex, 1);
    setVisibleCols(next);
    setActiveIndex(-1);
  };
  const onUp = () => {
    if (activeIndex <= 0) return;
    const next = visibleCols.slice();
    const [item] = next.splice(activeIndex, 1);
    next.splice(activeIndex - 1, 0, item);
    setVisibleCols(next);
    setActiveIndex((i) => i - 1);
  };
  const onDown = () => {
    if (activeIndex < 0 || activeIndex >= visibleCols.length - 1) return;
    const next = visibleCols.slice();
    const [item] = next.splice(activeIndex, 1);
    next.splice(activeIndex + 1, 0, item);
    setVisibleCols(next);
    setActiveIndex((i) => i + 1);
  };
  const onApplyCols = () => {
    localStorage.setItem("stockTransfer", JSON.stringify(visibleCols));
    onToggleColModal();
  };

  // Status update
  const onUpdateStatus = (data, e) => {
    const value = e.target.value;
    if (value === "NA") return;
    swal("Warning", "Sure You Want to Update Status", {
      buttons: { cancel: "No", catch: { text: "Yes", value: "Sure" } },
    }).then(async (v) => {
      if (v === "Sure") {
        try {
          setLoading(true);
          await Stockupdate(data?._id, { transferStatus: value });
          swal("success", "Status Updated Successfully");
          setViewOpen(false);
          // refresh list
          const user = JSON.parse(localStorage.getItem("userData") || "{}");
          const res = await _Get(Stock_trx_FtoW_List, user?.database);
          let list = (res?.Warehouse || []).reverse();
          if (warehouseIncharge) {
            const url = `${Warehouse_ListBy_id + user?._id}/${user?.database}`;
            const wRes = await _GetList(url);
            const assigned = (wRes?.Warehouse || []).map((w) => w?._id);
            list = list
              .filter(
                (it) =>
                  assigned.includes(it?.warehouseToId?._id) ||
                  assigned.includes(it?.warehouseFromId?._id)
              )
              .map((it) => ({
                ...it,
                ShowDropDown: assigned.includes(it?.warehouseToId?._id),
              }));
          } else if (superAdmin) {
            list = list.map((it) => ({ ...it, ShowDropDown: true }));
          } else {
            list = list.map((it) => ({ ...it, ShowDropDown: false }));
          }
          setRows(list);
        } catch (err) {
          // console.error(err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Render cells helper
  const renderCell = (col, r, i) => {
    if (col.render) return col.render(r, i);
    if (typeof col.value === "function") return col.value(r, i);
    if (col.valuePath) {
      return col.valuePath.split(".").reduce((o, k) => (o || {})[k], r) ?? "";
    }
    return r[col.field] ?? "";
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary" />
      </div>
    );
  }

  return (
    <>
      <a href="/#/dashboard" style={{ color: "green", paddingLeft: 10 }}>
        Home{" "}
      </a>{" "}
      / Lead
      <Card className="card-color" style={{ paddingBottom: 10 }}>
        <Row style={{ marginLeft: 3, marginRight: 3 }}>
          <Col>
            <h1
              className="float-left"
              style={{
                fontWeight: 600,
                textTransform: "uppercase",
                fontSize: 18,
                marginTop: 25,
              }}
            >
              Stock Trx Request list
            </h1>
          </Col>

          <Col style={{ marginTop: 25 }} lg="3" xl="3">
            <div className="table-input">
              <Input
                className="input-color"
                placeholder="search Item here..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </Col>

          <Col style={{ marginTop: 25 }} lg="1" xl="1">
            <Button
              style={{
                cursor: "pointer",
                float: "right",
                height: 30,
                color: "white",
                fontWeight: 600,
              }}
              className="categorysbutton45"
              onClick={() => navigate("/app/stock-transfer/create")}
            >
              <FaPlus size={13} /> Create
            </Button>
          </Col>

          <Col lg="1" style={{ marginTop: 25 }}>
            <span>
              <FaFilter
                style={{ cursor: "pointer" }}
                title="filter column"
                size={20}
                onClick={onToggleColModal}
                color="#39cccc"
                className="float-right"
              />
            </span>

            <span
              onMouseEnter={() => setIsExportOpen(true)}
              onMouseLeave={() => setIsExportOpen(false)}
              className="float-right"
              style={{ position: "relative", marginRight: 10 }}
            >
              <ImDownload
                title="download file"
                size={20}
                color="#39cccc"
                onClick={() => setIsExportOpen((s) => !s)}
                style={{ cursor: "pointer" }}
              />
              {isExportOpen && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    zIndex: 10,
                    border: "1px solid #39cccc",
                    backgroundColor: "white",
                  }}
                >
                  <h5
                    onClick={exportPDF}
                    style={{ cursor: "pointer" }}
                    className="mx-1 myactive mt-1"
                  >
                    .PDF
                  </h5>
                  <h5
                    onClick={exportCSV}
                    style={{ cursor: "pointer" }}
                    className="mx-1 myactive"
                  >
                    .CSV
                  </h5>
                  <h5
                    onClick={exportXLS}
                    style={{ cursor: "pointer" }}
                    className="mx-1 myactive"
                  >
                    .XLS
                  </h5>
                  <h5
                    onClick={exportXLSX}
                    style={{ cursor: "pointer" }}
                    className="mx-1 myactive"
                  >
                    .XLSX
                  </h5>
                  <h5
                    onClick={exportXML}
                    style={{ cursor: "pointer" }}
                    className="mx-1 myactive mb-1"
                  >
                    .XML
                  </h5>
                </div>
              )}
            </span>
          </Col>
        </Row>

        {/* Table */}
        <div style={{ padding: "0 12px", marginTop: "1rem" }}>
          <Table responsive bordered hover style={{ background: "white" }}>
            <thead>
              <tr>
                {visibleCols.map((c, idx) => (
                  <th key={idx} style={{ whiteSpace: "nowrap" }}>
                    {c.headerName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((r, i) => (
                <tr key={r?._id || i}>
                  {visibleCols.map((c, ci) => (
                    <td key={ci}>
                      {renderCell(c, r, i + (page - 1) * pageSize)}
                    </td>
                  ))}
                </tr>
              ))}
              {!pageData.length && (
                <tr>
                  <td
                    colSpan={visibleCols.length}
                    style={{ textAlign: "center" }}
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Pagination Controls */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Label className="mr-1">Rows:</Label>
              <Input
                type="select"
                style={{ width: 90, display: "inline-block" }}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 15, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Input>
            </div>
            <div>
              <Button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                size="sm"
                className="mr-1"
              >
                Prev
              </Button>
              <span>
                Page {page} of {totalPages}
              </span>
              <Button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                size="sm"
                className="ml-1"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </Card>
      {/* Column chooser */}
      <Modal
        isOpen={colModal}
        toggle={onToggleColModal}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={onToggleColModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h4>Available Columns</h4>
              <div
                style={{
                  border: "1px solid #eee",
                  maxHeight: 320,
                  overflow: "auto",
                  padding: 8,
                }}
              >
                {availableCols.map((col, i) => (
                  <div
                    key={i}
                    className="mt-1"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={
                        !!selectedPool.find(
                          (c) => c.headerName === col.headerName
                        )
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPool((p) =>
                            p.some((c) => c.headerName === col.headerName)
                              ? p
                              : [...p, col]
                          );
                        } else {
                          setSelectedPool((p) =>
                            p.filter((c) => c.headerName !== col.headerName)
                          );
                        }
                      }}
                    />
                    <span>{col.headerName}</span>
                  </div>
                ))}
              </div>
            </Col>

            <Col
              lg="2"
              md="2"
              sm="12"
              xl="2"
              xs="12"
              className="d-flex align-items-center justify-content-center"
            >
              <div>
                <FaArrowAltCircleRight
                  size={30}
                  style={{ cursor: "pointer" }}
                  onClick={onAddCols}
                />
                <div className="my-2" />
                <FaArrowAltCircleLeft
                  size={30}
                  style={{ cursor: "pointer" }}
                  onClick={onRemoveActive}
                />
              </div>
            </Col>

            <Col lg="6" md="6" sm="12" xl="6" xs="12">
              <Row>
                <Col lg="8" md="8" sm="12" xs="12">
                  <h4>Visible Columns</h4>
                  <div
                    style={{
                      border: "1px solid #eee",
                      maxHeight: 320,
                      overflow: "auto",
                      padding: 8,
                    }}
                  >
                    {visibleCols.map((col, i) => (
                      <div key={i} className="mt-1">
                        <h5
                          onClick={() => setActiveIndex(i)}
                          style={{
                            cursor: "pointer",
                            padding: "6px 8px",
                            borderRadius: 4,
                            backgroundColor:
                              activeIndex === i ? "#1877f2" : "#f7f7f7",
                            color: activeIndex === i ? "#fff" : "#333",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <IoMdRemoveCircleOutline
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = visibleCols.slice();
                              next.splice(i, 1);
                              setVisibleCols(next);
                              setActiveIndex(-1);
                            }}
                            size={22}
                            color="red"
                            className="mr-2"
                            style={{ cursor: "pointer" }}
                          />
                          {col.headerName}
                        </h5>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col
                  lg="4"
                  md="4"
                  sm="12"
                  xs="12"
                  className="d-flex align-items-center justify-content-center"
                >
                  <div>
                    <BsFillArrowUpSquareFill
                      size={30}
                      className="mb-2"
                      onClick={onUp}
                      style={{ cursor: "pointer" }}
                    />
                    <BsFillArrowDownSquareFill
                      size={30}
                      onClick={onDown}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col className="d-flex justify-content-center">
              <Button color="primary" onClick={onApplyCols}>
                Submit
              </Button>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
      {/* View / Invoice Modal */}
      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          {showBill ? "Challan Download" : "All Products"}
        </ModalHeader>
        <ModalBody className={showBill ? "p-1" : ""}>
          {viewRow && !showBill ? (
            <>
              <Row>
                <Col>
                  <Row>
                    <Col className="mt-1" lg="12">
                      <Label>From :</Label>
                      <h5>
                        {viewRow?.warehouseFromId?.warehouseName?.toUpperCase?.() ||
                          ""}
                      </h5>
                    </Col>
                    <Col className="mt-1" lg="12">
                      <Label>To :</Label>
                      <h5>
                        {viewRow?.warehouseToId?.warehouseName?.toUpperCase?.() ||
                          ""}
                      </h5>
                    </Col>
                    <Col className="mt-1" lg="12">
                      <Label>Current Status :</Label>
                      <div>
                        <Badge color="warning">
                          {String(viewRow?.transferStatus || "").toUpperCase()}
                        </Badge>
                      </div>
                    </Col>
                  </Row>
                </Col>

                <Col>
                  <Row>
                    {viewRow?.ShowDropDown &&
                      viewRow?.transferStatus !== "Completed" && (
                        <Col className="mt-1" lg="12">
                          <Label>Update Status :</Label>
                          <Input
                            type="select"
                            onChange={(e) => onUpdateStatus(viewRow, e)}
                          >
                            <option value="NA">--Select--</option>
                            <option value="Completed">Completed</option>
                            <option value="Pending">Pending</option>
                            <option value="Hold">Hold</option>
                          </Input>
                        </Col>
                      )}
                    {viewRow?.ShowDropDown && (
                      <Col className="mt-1" lg="12">
                        <Label>Download Challan :</Label>
                        <div className="d-flex justify-content-center">
                          <FaDownload
                            title="Download Challan"
                            onClick={() => setShowBill(true)}
                            color="blue"
                            style={{ cursor: "pointer" }}
                            size={30}
                          />
                        </div>
                      </Col>
                    )}

                    <Col className="mt-1" lg="12">
                      <Label>Date :</Label>
                      <div>{viewRow?.stockTransferDate || ""}</div>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="p-2">
                <Col>
                  <div className="d-flex justify-content-center">
                    <h4>Product Details</h4>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Table responsive bordered hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product Name</th>
                        <th>Primary Unit</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewRow?.productItems || []).map((it, i) => (
                        <tr key={i}>
                          <th scope="row">{i + 1}</th>
                          <td>{it?.productId?.Product_Title || ""}</td>
                          <td>{it?.primaryUnit || ""}</td>
                          <td>{it?.transferQty || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          ) : viewRow && showBill ? (
            <>
              <div className="d-flex justify-content-end">
                <Button color="danger" onClick={() => setShowBill(false)}>
                  X
                </Button>
              </div>
              <StockTrxInvoice ViewOneData={viewRow} />
            </>
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
};

export default WarehouseStockTransfer;
